import _ from 'lodash';
import dagre from '@moleculeone/dagre-plus';
import { V4 } from '@moleculeone/utils';
import { standardDrawing, getBoundingPoints, getBoundingBox, moveDrawnMolsToPosition } from '@moleculeone/draw';

import { getMolWithPositions, alignMol } from './mol';
import { assignLayeringOrder, assignParentPosition } from './order';
import { isCompound, getChildrenCompounds, getParentMoleculeNodeId } from './tree';

const addMolObject = (molCache, smilesToMol) => async node => {
  const { id } = node;
  let mol;

  if (molCache[id]) {
    mol = molCache[id];
  } else {
    const convertedMol = smilesToMol(node.smiles);
    mol = await getMolWithPositions(node.smiles, convertedMol);
  }

  return {
    ...node,
    mol,
  };
};

const addBoundingBox = margin => node => {
  return {
    ...node,
    bbox: getBoundingBox([node.drawnMol], margin),
  };
};

const createContextualPoint = (point, renderPointContext = () => {}) => {
  return {
    point,
    renderPointContext,
  };
};

export const prepareEdge = ({
  edge: edgeData,
  edges,
  nodes,
  graph,
  tree,
  margin,
  reactionLinkData,
  renderPointContext,
  synthesis,
}) => {
  const { v: productId, w: substrateId } = edgeData;
  const edge = graph.edge(edgeData);
  const { points } = edge;
  const {
    start: StartPoint,
    common: CommonPoint,
    break: BreakPoint,
    end: EndPoint,
    debug: DebugPoint,
  } = renderPointContext;

  const productBpts = getBoundingPoints([nodes[productId].drawnMol], margin);
  const substrateBpts = getBoundingPoints([nodes[substrateId].drawnMol], margin);

  const controlPoints = edges
    .filter(h => h.v === productId)
    .map(h => {
      const { points: hpts } = graph.edge(h);
      return V4.vect(hpts[1].x, hpts[1].y);
    });

  const currentReactionId = reactionLinkData[productId].currentReactionId;
  const currentReactionPrediction = reactionLinkData[productId].prediction;

  let startPoint;
  if (nodes[productId].children.length === 1) {
    startPoint = V4.vect(productBpts.max[0], V4.average(...controlPoints)[1]);
  } else {
    startPoint = V4.vect(productBpts.max[0], (productBpts.min[1] + productBpts.max[1]) / 2);
  }

  startPoint[0] -= 100;

  const commonPoint = V4.vect(V4.max(...controlPoints)[0], startPoint[1]);

  const breakPoint = V4.vect(commonPoint[0], points[2].y);

  const endPoint = V4.vect(substrateBpts.min[0], breakPoint[1]);

  const debugPoint = V4.vect(startPoint[0], startPoint[1] + 50);

  // Start point context (switch subtree)
  const renderStartPointContext = point => (
    <line
      x1={point[0]}
      y1={point[1]}
      x2={point[0]}
      y2={point[1]}
      fill="none"
      strokeWidth="3"
      markerStart="url(#arrowhead)"
    />
  );

  let renderCommonPointContext = null;
  if (CommonPoint) {
    renderCommonPointContext = (point, scale, padding) => {
      const reactionData = constructReactionData(tree, productId, currentReactionId);

      return (
        <CommonPoint
          point={point}
          scale={scale}
          padding={padding}
          productId={productId}
          reactionId={currentReactionId}
          reactionData={reactionData}
        />
      );
    };
  }

  // End point context (purchase compound)
  let renderEndPointContext = null;
  if (EndPoint) {
    renderEndPointContext = (point, scale, padding) => {
      return (
        <EndPoint
          point={point}
          scale={scale}
          padding={padding}
          productId={productId}
          substrateId={substrateId}
          reactionId={currentReactionId}
        />
      );
    };
  }

  let renderDebugPointContext = null;
  if (DebugPoint) {
    const fixedReactionPrediction = Number(currentReactionPrediction).toFixed(2);

    renderDebugPointContext = (point, scale, padding) => {
      const reactionData = constructReactionData(tree, productId, currentReactionId);

      return (
        <DebugPoint
          point={point}
          scale={scale}
          padding={padding}
          reactionId={currentReactionId}
          fixedReactionPrediction={fixedReactionPrediction}
          reactionData={reactionData}
          tree={tree}
          synthesis={synthesis}
        />
      );
    };
  }

  return {
    from: Number(substrateId),
    to: Number(productId),
    data: [
      createContextualPoint(startPoint, renderStartPointContext),
      createContextualPoint(commonPoint, renderCommonPointContext),
      createContextualPoint(breakPoint),
      createContextualPoint(endPoint, renderEndPointContext),
    ],
    debug: createContextualPoint(debugPoint, renderDebugPointContext),
  };
};

export const prepareNode = ({ acc, node, tree, mapping, canvas }) => {
  const parentId = getParentMoleculeNodeId(tree, node);

  const productMol = (acc[parentId] || {}).mol;
  const substrateMol = node.mol;
  const mapToProduct = (mapping[parentId] || []).reduce((acc, atom, atomId) => ({ ...acc, [atom]: atomId }), {});
  const substrateToMap = mapping[node.id] || [];

  const alignedMol = alignMol(productMol, substrateMol, mapToProduct, substrateToMap);
  const drawnMol = standardDrawing(alignedMol, [], canvas);

  return {
    ...acc,
    [node.id]: {
      id: node.id,
      doable: Boolean(node.doable),
      smiles: node.smiles,
      emoleculesId: node.emoleculesId,
      emoleculesPrice: node.emoleculesPrice,
      mol: alignedMol,
      drawnMol,
      children: node.children,
      unexpandedSuccessors: node.unexpandedSuccessors,
      predecessor: node.predecessor,
    },
  };
};

export const prepareNodes = async ({ tree, mapping, molCache, margin, smilesToMol, canvas }) => {
  const parsed = _.chain(tree).filter(isCompound).map(addMolObject(molCache, smilesToMol)).value();

  const resolved = await Promise.all(parsed);

  const nodes = _.chain(resolved)
    .mapValues(getChildrenCompounds(tree))
    .reduce((acc, node) => ({ ...acc, [node.id]: node }), {})
    .reduce((acc, node) => prepareNode({ acc, node, tree, mapping, canvas }), {})
    .mapValues(addBoundingBox(margin))
    .value();
  return nodes;
};

export const prepareGraphNodes = (nodes, graph) => {
  const result = _.chain(nodes)
    .values()
    .zip(graph.nodes())
    .reduce((acc, [node, v]) => {
      const {
        id,
        mol,
        drawnMol,
        bbox,
        children,
        doable,
        smiles,
        emoleculesId,
        emoleculesPrice,
        unexpandedSuccessors,
        predecessor,
      } = node;
      const { x, y } = graph.node(v);
      const targetPosition = V4.vect(x, y);

      const movedDrawnMol = moveDrawnMolsToPosition([drawnMol], targetPosition)[0];

      return {
        ...acc,
        [id]: {
          id,
          doable,
          mol,
          drawnMol: movedDrawnMol,
          bbox,
          children,
          smiles,
          emoleculesId,
          emoleculesPrice,
          unexpandedSuccessors,
          predecessor,
          targetPosition,
        },
      };
    }, {})
    .mapValues(node => ({ ...node, isBaseSubstrate: node.children.length === 0 }))
    .value();
  return result;
};

export const prepareGraphEdges = ({ nodes, graph, tree, margin, reactionLinkData, renderPointContext, synthesis }) => {
  return graph.edges().map((edge, _, edges) =>
    prepareEdge({
      edge,
      edges,
      nodes,
      graph,
      tree,
      margin,
      reactionLinkData,
      renderPointContext,
      synthesis,
    })
  );
};

export const createGraphNode =
  graph =>
  ({ node, allNodes }) => {
    const { width, height } = node.bbox;
    graph.setNode(node.id, { layeringorder: node.layeringOrder, width, height });
    return { node, allNodes };
  };

export const createGraphEdge = graph => (node, _index, allNodes) => {
  node.children.forEach(child => {
    const childNode = allNodes[child];
    const rank = node.layeringOrder + childNode.layeringOrder;
    graph.setEdge(node.id, child, { layeringorder: rank });
  });
  return node;
};

export const createInitialGraphLayout = (nodes, graph, mapping) => {
  _.forEach(nodes, _.flow([assignParentPosition, assignLayeringOrder, createGraphNode(graph, mapping)]));
  _.forEach(nodes, createGraphEdge(graph));
  dagre.layout(graph);
};

function constructReactionData(tree, productId, currentReactionId) {
  return {
    productSmiles: tree[productId].smiles,
    substrateSmiles: tree[currentReactionId].successors.map(sid => tree[sid].smiles),
    productToMap: tree[currentReactionId].productToMap,
    substratesToMap: tree[currentReactionId].substratesToMap,
  };
}
